import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { notificationsApi } from '../services/api'
import { useAuth } from './AuthContext'

const NotificationContext = createContext(null)

export function NotificationProvider({ children }) {
  const { user }               = useAuth()
  const [notifications, setNotifications] = useState([])
  const [unread, setUnread]    = useState(0)
  const [loading, setLoading]  = useState(false)
  const intervalRef            = useRef(null)

  const load = useCallback(async () => {
    if (!user) return
    try {
      const res = await notificationsApi.list()
      setNotifications(res.data ?? [])
      setUnread(res.unread ?? 0)
    } catch { /* silent */ }
  }, [user])

  // Poll every 60 seconds when user is logged in
  useEffect(() => {
    if (!user) {
      setNotifications([])
      setUnread(0)
      return
    }
    load()
    intervalRef.current = setInterval(load, 60_000)
    return () => clearInterval(intervalRef.current)
  }, [user, load])

  const markRead = async (id) => {
    try {
      await notificationsApi.markRead(id)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      setUnread(prev => Math.max(0, prev - 1))
    } catch { /* silent */ }
  }

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead()
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })))
      setUnread(0)
    } catch { /* silent */ }
  }

  const remove = async (id) => {
    const n = notifications.find(x => x.id === id)
    try {
      await notificationsApi.remove(id)
      setNotifications(prev => prev.filter(x => x.id !== id))
      if (n && !n.read_at) setUnread(prev => Math.max(0, prev - 1))
    } catch { /* silent */ }
  }

  return (
    <NotificationContext.Provider value={{ notifications, unread, loading, load, markRead, markAllRead, remove }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider')
  return ctx
}
